import { Box, CircularProgress } from "@mui/material";
import { Navigate, Outlet } from "react-router-dom";
import { useAppSelector } from "@/app/store";
import { STATUS } from "@/constants";
import {
  selectAccessibleTabs,
  selectCurrentUserStatus,
} from "@/features/users/model/usersSlice";
import type { NavTabName } from "@/shared/ui/layout/navTabs";

/**
 * Authorization guard for a route gated behind a navigation tab. Renders the
 * route only when `tab` is present in the logged-in user's `access.tabs`;
 * otherwise redirects to Home. While `access` is still loading it shows a
 * loader so a permitted user is never redirected before access is known.
 */
export function RequireTab({ tab }: { tab: NavTabName }) {
  const accessibleTabs = useAppSelector(selectAccessibleTabs);
  const currentUserStatus = useAppSelector(selectCurrentUserStatus);

  if (currentUserStatus === STATUS.IDLE || currentUserStatus === STATUS.LOADING) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        aria-busy
      >
        <CircularProgress />
      </Box>
    );
  }

  const isAllowed = accessibleTabs.some((accessibleTabName) => accessibleTabName === tab);
  if (!isAllowed) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}
